import type { z } from "zod";

import type { AgentCapabilityKey } from "@phoenix/agent/extensions/capabilities";
import type { AgentToolUIBehavior } from "@phoenix/agent/extensions/registry/defineTool";
import type { AgentClientActionResult } from "@phoenix/store/agentStore";

/**
 * How an operation affects app state, and therefore how the `execute_browser_action`
 * runtime treats it:
 * - `read` — returns data, no state change; scripts may call freely.
 * - `write` — mutates client state synchronously (e.g. set the time range).
 * - `approval` — stages a pending change whose promise resolves only after
 *   the user accepts or rejects it. The script runtime keeps awaiting (and
 *   pauses the script's wall-clock budget) until the user decides.
 */
export type UIOperationKind = "read" | "write" | "approval";

/**
 * Where an operation becomes available when its handler is not currently
 * mounted. Surfaced by `search_browser_actions` and by not-mounted dispatch errors so the
 * agent can navigate instead of dead-ending.
 */
export type UIOperationAvailability = {
  /** Human/model-readable hint, e.g. "the Playground page (/playground)". */
  routeHint: string;
};

/**
 * The result every operation resolves with. `output` must be
 * JSON-serializable — it crosses the script worker's postMessage boundary
 * and is embedded in the `execute_browser_action` tool output.
 */
export type UIOperationResult = AgentClientActionResult;

/**
 * Stable machine-readable failure categories, so scripts branch on `code`
 * instead of string-matching English error prose. Dispatch stamps the
 * generic categories at its choke points; operation handlers supply the
 * domain-specific ones on their own failures. Extend the union when a new
 * domain failure needs to be branchable — and keep the human-readable
 * `error` string alongside; the code categorizes, the prose explains.
 */
export type UIOperationErrorCode =
  // -- stamped by dispatch --
  /** The operation name is not in the catalog. */
  | "UNKNOWN_OPERATION"
  /** A capability required by the operation is disabled. */
  | "CAPABILITY_DISABLED"
  /** The operation requires an active agent session. */
  | "NO_SESSION"
  /** The operation's UI surface is not mounted on the current page. */
  | "NOT_AVAILABLE"
  /** The input failed the operation's schema validation. */
  | "INVALID_INPUT"
  /** The handler threw instead of returning a result. */
  | "HANDLER_ERROR"
  // -- supplied by operation handlers --
  /** A referenced entity (instance, message, tool, provider) does not exist. */
  | "NOT_FOUND"
  /** An `expectedRevision` token does not match the current revision. */
  | "STALE_REVISION"
  /** A read of run output before any run produced output. */
  | "NO_RUN_OUTPUT";

/**
 * Per-call context handed to an operation handler by dispatch.
 *
 * `callId` uniquely identifies this invocation — approval operations use it
 * to key their pending-approval entries (the role the AI SDK `toolCallId`
 * played when each operation was its own tool call).
 */
export type UIOperationCallContext = {
  callId: string;
  sessionId: string | null;
};

/**
 * The self-describing catalog entry for one client-side operation.
 *
 * This is the single source of truth that replaces the three parallel
 * artifacts each tool used to keep in sync: the server-side JSON schema in
 * `src/phoenix/server/agents/capabilities/tools/external/<name>.py`, the
 * hand-rolled `parseInput` in `app/src/agent/tools/<name>/parsers.ts`, and
 * the `defineClientActionTool` config. The model-facing signature and the
 * runtime validator are both derived from `inputSchema`.
 */
export type UIOperationDescriptor<TSchema extends z.ZodType = z.ZodType> = {
  /**
   * Namespaced, dot-separated operation name (e.g. `timeRange.set`). The
   * namespace is the property path scripts use: `ui.timeRange.set(...)`.
   */
  name: string;
  /** Model-facing description; what used to live in the Python `DESCRIPTION`. */
  description: string;
  /** Zod schema for the operation input; the only schema definition anywhere. */
  inputSchema: TSchema;
  /**
   * Zod schema describing the success `output` shape, rendered into the
   * operation's `search_browser_actions` signature as `Promise<UIResult<T>>`. This is
   * documentation, not a runtime gate — dispatch does not validate outputs
   * against it, so a drifted schema misleads the model but never turns a
   * working operation into a user-facing failure. Approximate shapes
   * (`z.unknown()` leaves for deep subtrees) are better than nothing: the
   * top-level field names are what scripts branch on.
   */
  outputSchema?: z.ZodType;
  operationKind: UIOperationKind;
  /**
   * Marks an operation whose handler legitimately awaits completion of work
   * that can outlast the script's wall-clock budget (e.g. a playground run).
   * Like `approval`-kind calls, the budget pauses while the call is in
   * flight.
   */
  longRunning?: boolean;
  /**
   * Marks an `approval`-kind operation that stages a user-facing approval
   * card even in bypass edit mode (e.g. navigation, which always asks).
   * Dispatch uses this to know a card is about to appear regardless of the
   * edit permission, so it can open the host tool part.
   */
  alwaysRequiresApproval?: boolean;
  /** Capability keys that must be enabled for this operation to dispatch. */
  requiredCapabilities?: AgentCapabilityKey[];
  /** Whether an active agent session is required to dispatch. */
  requireSession?: boolean;
  /** Chat card rendering hints, keyed per inner operation call. */
  UIBehavior?: AgentToolUIBehavior;
  availability?: UIOperationAvailability;
  /** Fallback success output when the handler omits one. */
  defaultSuccessOutput?: string;
};

/**
 * The callable a mounted component registers for an operation. Receives
 * input already validated against the descriptor's `inputSchema`, plus the
 * per-call context. Approval handlers return a promise that stays pending
 * until the user accepts or rejects.
 */
export type UIOperationHandler<TInput> = (
  input: TInput,
  context: UIOperationCallContext
) => Promise<UIOperationResult>;

/**
 * Resolves an in-flight operation call. Approval binders receive this in
 * place of the retired `addToolOutput` sender: instead of writing tool
 * output for a dedicated tool call, accept/reject/cancel resolve the promise
 * the calling `execute_browser_action` script is awaiting.
 */
export type UIOperationResultEmitter = (result: UIOperationResult) => void;

/**
 * Narrow an unknown handler `context` argument to {@link UIOperationCallContext}.
 * Dispatch always constructs the context, so a mismatch means the handler was
 * invoked outside the operation dispatch path.
 */
export function parseUIOperationCallContext(
  context: unknown
): UIOperationCallContext | null {
  if (typeof context !== "object" || context === null) {
    return null;
  }
  const candidate = context as { callId?: unknown; sessionId?: unknown };
  if (typeof candidate.callId !== "string") {
    return null;
  }
  if (
    candidate.sessionId !== null &&
    candidate.sessionId !== undefined &&
    typeof candidate.sessionId !== "string"
  ) {
    return null;
  }
  return {
    callId: candidate.callId,
    sessionId: candidate.sessionId ?? null,
  };
}

/**
 * Identity helper that ties the descriptor's inferred input type to its
 * schema so `registerUIOperations` can enforce handler/schema agreement.
 */
export function defineUIOperation<TSchema extends z.ZodType>(
  descriptor: UIOperationDescriptor<TSchema>
): UIOperationDescriptor<TSchema> {
  return descriptor;
}
